// Operators occasionally lose all mouse input while the page keeps running: the
// renderer is alive (the identification timer keeps ticking, the page still
// scrolls), but no click lands anywhere and only a reload recovers. Static
// analysis ruled out every in-page cause, and it has never reproduced on a
// developer machine, so this watches for the state in production and reports
// what it can actually see -- instead of asking operators to run the DevTools
// snippet in debug/README.md, which nobody runs while their workflow is stuck.
//
// It distinguishes the only two shapes the failure can have:
//
//   "input-lost"          pointer events stop arriving while wheel or keyboard
//                         events keep coming. Input is being lost below the
//                         page, in Chrome or Windows, and no app-side change
//                         can prevent it.
//   "pointer-retargeted"  pointer events do arrive, but on an element that is
//                         not under the cursor. That is pointer capture, which
//                         routes every click in the tab to one element and is
//                         ours to fix.
//
// It never calls preventDefault and never mutates the page. All listeners are
// passive.
//
// First 24h in production (2026-09-17, 48 events): 46 were "pointer-retargeted"
// and every one of them was a false positive -- a `lastPointerTarget` that Vue
// had unmounted, which can never match what is under the cursor and so
// mismatched forever. Nobody's mouse was stuck. Both "input-lost" reports, from
// two different operators, landed on the identification photo at
// ZOOM_LEVELS.ENHANCED with no capture held anywhere, which killed the theory
// that our own setPointerCapture was the holder. What is left points below the
// page: IT reports every affected machine is a Ryzen CPU with an AMD GPU, and
// Chrome targets mouse events from compositor hit-test data while keyboard goes
// straight to the focused widget and wheel scrolling is latched to a scroll
// node -- so a wedged GPU process loses exactly mouse input and nothing else.
//
// The fields added for that round are `gpuRenderer`/`gpuVendor` (splits the
// affected population by hardware), `lastWheelButtons` (wheel events keep
// arriving during the failure and carry the one field that can confirm or kill
// "a mouse button is still held down"), and `pointerMovedSilentlyInWindow`
// (grades the evidence behind each input-lost report -- see recordActivity).
//
// Round three (2026-09-21), after a full 24h set of 12 events from 7 operators:
// none of them was the reported failure and eleven were artefacts of how this
// file measures. What changed here:
//
//   * It no longer touches the page. `attemptRecovery` released whatever
//     pointer capture `lastPointerTarget` held, and three of the twelve
//     released the photo's own capture in the middle of a pan -- the component
//     ends the drag on `lostpointercapture`, so the operator's pan died with
//     nothing on screen to explain it, on a zoomed photo, which is exactly what
//     they complain about. A stuck capture of ours was disproven long before
//     that (never once held during an input-lost), so the release had no
//     remaining justification and the holder is named by `lastTargetHasCapture`
//     without releasing anything.
//   * "input-lost" now needs the windowed measurement, not the latch. Six of
//     the eight input-lost reports had `silentMoveMaxPx: 0` with wheels landing
//     throughout -- a parked mouse during a long scroll, which produces no
//     pointer events because nothing moved. One ran 21.6s that way, longer than
//     any real report, which is also why severity must never be graded on
//     silence alone.
//   * "pointer-retargeted" now ignores a held button and a scrolled sample.
//     Three of its four reports were a legitimate drag whose cursor left the
//     photo, and the fourth hit-tested a point the page had scrolled out from
//     under. Both are normal.
//
// The 12 events are also the end of the hardware lead: AMD APUs, an AMD
// discrete card, two Intel Arc laptops and one machine on Microsoft Basic
// Render Driver, across Chrome 152/153, Edge 153 and Firefox 156. No vendor,
// driver or browser is common to them.

const SIGNATURE = {
  inputLost: "input-lost",
  pointerRetargeted: "pointer-retargeted"
};

// Pointer silence long enough that the operator has noticed and started trying
// things. Below ~3s this fires on someone who simply paused.
const POINTER_SILENCE_MS = 5000;
// Consecutive retargeted moves before we trust the mismatch. One or two are
// expected during a legitimate drag, which is exactly what capture is for.
const MISMATCH_LIMIT = 5;
const EVALUATE_THROTTLE_MS = 250;
// How far the cursor must have travelled before we believe pointer events are
// being lost rather than simply not happening. 12px was below frame
// granularity and therefore meaningless: the browser delivers one pointermove
// per frame, so 12px between frames is 720px/s -- an ordinary flick, which is
// how a parked mouse ended up latched as "moved silently" in six of eight
// production reports.
const SILENT_MOVE_PX = 48;
// A mismatch has to survive this long, not merely be counted this often. The
// counter climbs once per evaluate() sample, and evaluate is throttled to
// 250ms rather than the 1s tick, so MISMATCH_LIMIT alone convicted after 1.25s
// -- shorter than any deliberate drag past the edge of the photo.
const MISMATCH_MS = 5000;
// The same signature twice inside this window is the same condition flapping,
// not two freezes. Two production reports 14s apart carried identical evidence
// and between them burned two thirds of a page load's budget.
const REPORT_COOLDOWN_MS = 30000;
// The failure removes the very events that would drive detection, so evaluation
// cannot be event-driven alone: pointer events have stopped by definition, and
// waiting for the next wheel or keystroke means waiting for the operator to
// scroll. A slow tick makes the banner appear on its own once the evidence is
// in, at the cost of one throttled evaluation per second.
const TICK_MS = 1000;
// Reported controls are capped so a wide screen cannot produce a huge payload.
const MAX_CONTROLS = 40;
// Per signature per page load, so a persistent freeze cannot flood telemetry.
// Budgeted per signature because a shared budget let false positives starve the
// signal: one production session spent two of its three reports on retargeting
// that never happened, then went silent for the rest of a 2.8h shift.
const MAX_REPORTS = 3;
// Application Insights truncates a custom property value at 8192 characters,
// and a truncated value is unparseable JSON -- which would make the whole
// report worthless. Kept under with room to spare.
const MAX_PROPERTY_CHARS = 7000;
// The controls that do NOT receive their own click are the interesting ones. A
// handful names the interceptor without shipping a description of the screen.
const MAX_BLOCKED_EXAMPLES = 5;
// Renderer strings are long and vendor-formatted; the vendor and model are at
// the front and that is all the diagnosis needs.
const MAX_HARDWARE_CHARS = 120;
// A simulation must never be able to leave someone stuck without a mouse.
const SIMULATION_MAX_MS = 60000;

// `pointercancel` is here to be counted, not just to reset the silence: the
// production sets are short 1-5 pointerups against their pointerdowns, and a
// cancel is the only thing that ends a pointer stream without one. Until it was
// tracked, a native drag and a lost pointer looked identical from in here.
const POINTER_TYPES = [
  "pointermove",
  "pointerdown",
  "pointerup",
  "pointercancel"
];
const ACTIVITY_TYPES = ["wheel", "keydown"];
// Swallowed by a simulation so the page genuinely stops responding. Wheel and
// keyboard are deliberately left alone: scrolling and typing keep working in
// the real failure, and the operator needs a way out of the demo.
const SIMULATED_BLOCK_TYPES = [
  "pointermove",
  "pointerdown",
  "pointerup",
  "mousemove",
  "mousedown",
  "mouseup",
  "click",
  "dblclick",
  "auxclick",
  "contextmenu"
];

let watchdog = null;

function describeElement(element) {
  if (!(element instanceof Element)) {
    return null;
  }

  return {
    tag: element.tagName,
    classes: (element.getAttribute("class") || "").slice(0, 160)
  };
}

function inspectElement(element) {
  if (!(element instanceof Element)) {
    return null;
  }

  const style = getComputedStyle(element);
  const bounds = element.getBoundingClientRect();
  return {
    ...describeElement(element),
    cursor: style.cursor,
    pointerEvents: style.pointerEvents,
    display: style.display,
    visibility: style.visibility,
    opacity: style.opacity,
    position: style.position,
    zIndex: style.zIndex,
    transform: style.transform,
    bounds: {
      x: Math.round(bounds.x),
      y: Math.round(bounds.y),
      width: Math.round(bounds.width),
      height: Math.round(bounds.height)
    }
  };
}

// A click the browser synthesised from Enter or Space carries no click count
// and no pointer type; a click from a real mouse always has `detail >= 1`.
// Only activation events can be keyboard-driven -- mousedown, pointermove and
// friends never are, so they are always blocked by a simulation.
const KEYBOARD_ACTIVATABLE = ["click", "auxclick", "contextmenu"];

function isKeyboardActivated(event) {
  if (!KEYBOARD_ACTIVATABLE.includes(event.type)) {
    return false;
  }

  return !event.detail && !event.pointerType;
}

// The watchdog must never be the thing that breaks the page, and
// elementFromPoint is missing in some non-browser environments.
function hitTest(x, y) {
  if (typeof document.elementFromPoint !== "function") {
    return null;
  }

  try {
    return document.elementFromPoint(x, y);
  } catch (error) {
    return null;
  }
}

function isOnScreen(element) {
  const rect = element.getBoundingClientRect();
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.right > 0 &&
    rect.bottom > 0 &&
    rect.left < window.innerWidth &&
    rect.top < window.innerHeight
  );
}

// The decisive measurement: for each visible control, does a click at its own
// centre actually reach it? `receivesHit: false` names whatever is intercepting.
function sampleControls() {
  return [...document.querySelectorAll("button, input, textarea, select, a")]
    .filter(isOnScreen)
    .slice(0, MAX_CONTROLS)
    .map(element => {
      const rect = element.getBoundingClientRect();
      const x = (Math.max(0, rect.left) + Math.min(window.innerWidth, rect.right)) / 2;
      const y = (Math.max(0, rect.top) + Math.min(window.innerHeight, rect.bottom)) / 2;
      const hit = hitTest(x, y);
      return {
        control: describeElement(element),
        disabled: !!element.disabled,
        hit: inspectElement(hit),
        // An ancestor under the sample point means the control is clipped by a
        // scrollport, or the point fell in a gap in its own subtree -- never a
        // foreign interceptor, which is the only thing this measurement is for.
        // Every false blocker in production was the identification grid, an
        // ancestor of the button it was supposedly blocking.
        receivesHit:
          element === hit || element.contains(hit) || hit.contains(element)
      };
    });
}

// Read once, after boot and inside an idle callback: this is a GPU allocation
// on machines whose GPU driver is the suspect, so it must never sit in front of
// anything the operator is waiting for. Deliberately not read at report time --
// a wedged GPU process hands back a software fallback, which would name the
// wrong hardware in the one report that matters. The context is requested with
// nothing attached to it and dropped immediately.
function readHardware() {
  const hardware = {
    hardwareConcurrency: navigator.hardwareConcurrency ?? null,
    gpuVendor: null,
    gpuRenderer: null
  };

  // Absent in jsdom, so this also keeps the test run quiet.
  if (typeof window.WebGLRenderingContext !== "function") {
    return hardware;
  }

  try {
    const canvas = document.createElement("canvas");
    canvas.width = 1;
    canvas.height = 1;
    const gl = canvas.getContext("webgl", {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      powerPreference: "low-power"
    });
    if (!gl) {
      return hardware;
    }

    const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
    const vendor = gl.getParameter(
      debugInfo ? debugInfo.UNMASKED_VENDOR_WEBGL : gl.VENDOR
    );
    const renderer = gl.getParameter(
      debugInfo ? debugInfo.UNMASKED_RENDERER_WEBGL : gl.RENDERER
    );
    hardware.gpuVendor = String(vendor || "").slice(0, MAX_HARDWARE_CHARS);
    hardware.gpuRenderer = String(renderer || "").slice(0, MAX_HARDWARE_CHARS);
    gl.getExtension("WEBGL_lose_context")?.loseContext();
  } catch (error) {
    // A blocked or unavailable context says nothing about the mouse.
  }

  return hardware;
}

// Application Insights delivered one production report three times -- twice on
// the same millisecond and once again an hour and 43 minutes later, byte for
// byte. Counting events therefore overstates freezes, and there was no way to
// tell duplicates from a genuine repeat except by eye. An id per report makes
// the query trustworthy.
function newReportId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch (error) {
    // Fall through to the cheap id.
  }

  return `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

// The direct measurement behind the "pointer-retargeted" heuristic, which only
// ever inferred a capture from a mismatch. Recorded rather than required: a
// capture taken by the browser itself, for a native drag, is invisible here, so
// a false would not be proof of innocence -- but a true names the holder.
function hasCaptureOnLastTarget(state) {
  const holder = state.lastPointerTarget;
  if (!(holder instanceof Element) || state.lastPointerId === null) {
    return null;
  }

  try {
    return !!holder.hasPointerCapture?.(state.lastPointerId);
  } catch (error) {
    return null;
  }
}

// Deliberately excludes anything the operator could have typed or any image
// data -- element tags, classes, geometry and computed styles only.
function buildSnapshot(state, signature) {
  const { lastPoint } = state;
  return {
    signature,
    simulated: !!state.simulation,
    userAgent: navigator.userAgent,
    devicePixelRatio: window.devicePixelRatio,
    viewport: { width: window.innerWidth, height: window.innerHeight },
    elapsedMs: Math.round(state.now() - state.startedAt),
    hasFocus: document.hasFocus(),
    visibility: document.visibilityState,
    activeElement: describeElement(document.activeElement),
    pointerLockElement: describeElement(document.pointerLockElement),
    ...(state.hardware || {}),
    counts: { ...state.counts },
    // 0 on a healthy wheel event. Anything else is a button the browser still
    // believes is held down, which is the whole of the surviving explanation.
    lastWheelButtons: state.lastWheelButtons,
    wheelsWithButtonsHeld: state.wheelsWithButtonsHeld,
    // What the last pointer event we did receive reported, for the same reason.
    lastPointerButtons: state.lastPointerButtons,
    msSinceLastPointerEvent:
      state.lastPointerEventAt === null
        ? null
        : Math.round(state.now() - state.lastPointerEventAt),
    msSinceLastActivity:
      state.lastActivityAt === null
        ? null
        : Math.round(state.now() - state.lastActivityAt),
    retargetedMismatches: state.mismatches,
    pointerMovedSilently: state.pointerMovedSilently,
    pointerMovedSilentlyInWindow: state.pointerMovedSilentlyInWindow,
    silentMoveMaxPx: state.silentMoveMaxPx,
    silentWheelSamples: state.silentWheelSamples,
    lastPoint: lastPoint && { x: lastPoint.x, y: lastPoint.y },
    lastPointerTarget: describeElement(state.lastPointerTarget),
    // False means the element is no longer in the document, which is the whole
    // explanation for a retargeting report and nothing to do with the mouse.
    lastPointerTargetConnected:
      state.lastPointerTarget instanceof Element
        ? state.lastPointerTarget.isConnected
        : null,
    lastTargetHasCapture: hasCaptureOnLastTarget(state),
    hitAtLastPoint:
      lastPoint && inspectElement(hitTest(lastPoint.x, lastPoint.y)),
    controls: sampleControls()
  };
}

// The console keeps the full snapshot; the wire gets a compact version. The
// fields that decide the diagnosis are sent flat as well as inside the JSON, so
// they can be filtered and charted in a query without parsing anything.
function buildTelemetryProperties(snapshot) {
  const blocked = snapshot.controls.filter(control => !control.receivesHit);
  const detail = {
    ...snapshot,
    controls: undefined,
    controlsSampled: snapshot.controls.length,
    controlsBlocked: blocked.length,
    blockedExamples: blocked.slice(0, MAX_BLOCKED_EXAMPLES)
  };

  let serialised = JSON.stringify(detail);
  if (serialised.length > MAX_PROPERTY_CHARS) {
    serialised = JSON.stringify({
      ...detail,
      blockedExamples: [],
      blockedExamplesDropped: true
    });
  }

  return {
    reportId: snapshot.reportId,
    reportIndex: snapshot.reportIndex,
    signature: snapshot.signature,
    simulated: snapshot.simulated,
    msSinceLastPointerEvent: snapshot.msSinceLastPointerEvent,
    pointerMovedSilently: snapshot.pointerMovedSilently,
    pointerMovedSilentlyInWindow: snapshot.pointerMovedSilentlyInWindow,
    silentMoveMaxPx: snapshot.silentMoveMaxPx,
    silentWheelSamples: snapshot.silentWheelSamples,
    lastWheelButtons: snapshot.lastWheelButtons,
    wheelsWithButtonsHeld: snapshot.wheelsWithButtonsHeld,
    lastPointerButtons: snapshot.lastPointerButtons,
    gpuVendor: snapshot.gpuVendor,
    gpuRenderer: snapshot.gpuRenderer,
    hardwareConcurrency: snapshot.hardwareConcurrency,
    lastTargetConnected: snapshot.lastPointerTargetConnected,
    lastTargetHasCapture: snapshot.lastTargetHasCapture,
    retargetedMismatches: snapshot.retargetedMismatches,
    controlsSampled: detail.controlsSampled,
    controlsBlocked: detail.controlsBlocked,
    hitTag: snapshot.hitAtLastPoint?.tag,
    hitClasses: snapshot.hitAtLastPoint?.classes,
    hitCursor: snapshot.hitAtLastPoint?.cursor,
    hitPointerEvents: snapshot.hitAtLastPoint?.pointerEvents,
    hasFocus: snapshot.hasFocus,
    snapshot: serialised
  };
}

function report(state, signature) {
  const sentForSignature = state.reportsBySignature[signature] || 0;
  if (sentForSignature >= MAX_REPORTS) {
    return;
  }

  // A condition that clears and re-trips within seconds is one event with a
  // gap in it. Reporting both spends a budget that a long shift cannot refill.
  const lastSent = state.lastReportAtBySignature[signature];
  if (
    lastSent !== undefined &&
    state.now() - lastSent < REPORT_COOLDOWN_MS &&
    !state.simulation
  ) {
    return;
  }
  state.lastReportAtBySignature[signature] = state.now();
  state.reportsBySignature[signature] = sentForSignature + 1;
  state.reports += 1;

  const snapshot = buildSnapshot(state, signature);
  snapshot.reportId = newReportId();
  snapshot.reportIndex = state.reports;

  state.onFreeze?.(snapshot);
  state.trackEvent?.({
    name: "input-freeze-detected",
    properties: buildTelemetryProperties(snapshot)
  });
}

function detectSignature(state) {
  const now = state.now();

  // A backgrounded or unfocused tab legitimately receives no pointer events.
  if (!document.hasFocus() || document.visibilityState !== "visible") {
    return null;
  }

  // Pointer events are arriving by definition here -- they are just landing on
  // the wrong element -- so their own arrival is the proof the operator is
  // present. Requiring a recent wheel or keystroke on top of that would only
  // delay detection for someone who is moving the mouse and nothing else.
  // A button held down means the operator is dragging, and a drag is the one
  // case where the event target is *supposed* to stop matching what is under
  // the cursor -- that is what pointer capture does. Three of the four
  // retargeting reports in production were a photo pan whose cursor had left
  // the photo, and the watchdog answered by releasing the capture underneath
  // it.
  const buttonHeld = !!state.lastPointerButtons;
  if (
    !buttonHeld &&
    state.mismatches >= MISMATCH_LIMIT &&
    state.mismatchSince !== null &&
    now - state.mismatchSince >= MISMATCH_MS
  ) {
    return SIGNATURE.pointerRetargeted;
  }

  // Never having seen a pointer event is not the same as having lost them: an
  // operator who has only scrolled since the page loaded is not frozen.
  if (state.lastPointerEventAt === null) {
    return null;
  }
  // Silence alone is not evidence, and nor is recent activity -- what we need is
  // positive proof the cursor moved while we heard nothing about it. Only a
  // present operator can produce that, so it subsumes any liveness check: an
  // operator who walked away generates no wheel events and is never reported.
  //
  // The proof has to come from inside the silence. `pointerMovedSilently`
  // latches on one wheel and survives until the next pointer event, so an
  // operator who flicks the mouse once and then scrolls a long list with it
  // parked stays latched for as long as they keep scrolling: six of the eight
  // production input-lost reports were that, one of them for 21.6s. Comparing
  // consecutive wheels to each other cannot be fooled the same way, because a
  // parked mouse reports the same position every time.
  if (
    state.pointerMovedSilently &&
    state.pointerMovedSilentlyInWindow &&
    now - state.lastPointerEventAt > POINTER_SILENCE_MS
  ) {
    return SIGNATURE.inputLost;
  }

  return null;
}

function evaluate(state) {
  const now = state.now();
  if (now - state.lastEvaluatedAt < EVALUATE_THROTTLE_MS) {
    return;
  }
  state.lastEvaluatedAt = now;

  // A pointer-retargeted simulation seeds the counter itself; sampling the real
  // hit target would immediately reset it.
  if (state.simulation !== SIGNATURE.pointerRetargeted) {
    trackRetargeting(state);
  }

  const signature = detectSignature(state);
  if (!signature) {
    // The condition has cleared. Releasing the latch is what makes a second
    // freeze in the same shift visible at all -- it used to be reported once
    // per page load and never again, so a session with fifteen freezes and a
    // session with one looked identical. MAX_REPORTS still caps the volume.
    if (state.activeSignature && !state.simulation) {
      state.activeSignature = null;
      state.onRecover?.();
    }
    return;
  }
  if (state.activeSignature === signature) {
    return;
  }

  state.activeSignature = signature;
  report(state, signature);
}

// Pointer capture is invisible in the DOM, so the only way to see it is that
// the event target stops matching what is under the cursor.
//
// Called only from the throttled evaluate(), never per event. elementFromPoint
// forces a style and layout flush when the DOM is dirty, and a zoom drag writes
// a transform on every pointermove -- running this per event measured 297us
// each, about 1.8 frames, on the identification screen. Sampling the last known
// point a few times a second costs nothing and detects a stuck capture just as
// well, since a capture does not come and go between samples.
function trackRetargeting(state) {
  // Paired deliberately with the point from the last *pointer* event: wheel
  // events also write `lastPoint`, and judging a pointer target against a
  // position it never reported is how a mismatch appears out of nothing.
  const { lastPointerPoint, lastPointerTarget } = state;
  if (!lastPointerPoint || !(lastPointerTarget instanceof Element)) {
    return;
  }

  // The signature means "pointer events do arrive, but on the wrong element",
  // so only an arriving pointer event is evidence for it. Sampling on the tick
  // instead made a parked mouse enough to convict: the counter climbed against
  // a cached target nothing was refreshing, 1/s, and tripped the threshold in
  // five seconds. That was 46 of the first 48 production reports.
  if (!state.pointerEventsSinceSample) {
    return;
  }
  state.pointerEventsSinceSample = 0;

  // The hit test asks what is at `lastPointerPoint` *now*, and this app scrolls
  // inside `.content`, so a wheel between that pointer event and this sample
  // moves a different row under the same coordinates. One production report was
  // exactly that: a connected `SPAN.field-input` judged against the `DIV.field`
  // of another row. The point is only valid for the DOM that produced it.
  if (state.wheelsSinceLastPointer > 0) {
    return;
  }

  // An element Vue has unmounted is under nothing and contains nothing, so it
  // can never satisfy the check below and mismatches forever. The
  // identification view swaps its whole subtree on every status change and
  // every pushed identification -- routine, and the last thing the operator
  // touched is detached by it several times a shift.
  if (!lastPointerTarget.isConnected) {
    state.mismatches = 0;
    state.mismatchSince = null;
    return;
  }

  const hit = hitTest(lastPointerPoint.x, lastPointerPoint.y);
  if (!hit) {
    return;
  }
  if (
    lastPointerTarget === hit ||
    lastPointerTarget.contains(hit) ||
    hit.contains(lastPointerTarget)
  ) {
    state.mismatches = 0;
    state.mismatchSince = null;
    return;
  }

  if (state.mismatchSince === null) {
    state.mismatchSince = state.now();
  }
  state.mismatches += 1;
}

function recordPointer(state, event) {
  state.counts[event.type] = (state.counts[event.type] || 0) + 1;

  // While simulating "input-lost" the watchdog ignores pointer events, which is
  // what the real failure looks like from in here.
  if (state.simulation === SIGNATURE.inputLost) {
    return;
  }

  state.lastPointerEventAt = state.now();
  state.pointerMovedSilently = false;
  // A pointer event is the only evidence that can convict of retargeting, and
  // it also ends whatever silence was being measured.
  state.pointerEventsSinceSample += 1;
  state.pointerMovedSilentlyInWindow = false;
  state.silentMoveMaxPx = 0;
  state.silentWheelSamples = 0;
  state.silentWindowPoint = null;
  state.wheelsSinceLastPointer = 0;
  if (typeof event.buttons === "number") {
    state.lastPointerButtons = event.buttons;
  }
  if (typeof event.clientX === "number") {
    state.lastPointerPoint = { x: event.clientX, y: event.clientY };
  }
  state.lastPointerId = event.pointerId ?? null;
  state.lastPointerTarget =
    state.simulation === SIGNATURE.pointerRetargeted
      ? state.simulationDecoy
      : event.target;

  if (typeof event.clientX === "number") {
    state.lastPoint = { x: event.clientX, y: event.clientY };
  }

  evaluate(state);
}

function recordActivity(state, event) {
  state.counts[event.type] = (state.counts[event.type] || 0) + 1;
  state.lastActivityAt = state.now();
  if (event.type === "wheel") {
    state.wheelsSinceLastPointer += 1;
  }

  // Wheel events keep arriving right through the failure, which makes them the
  // only channel still carrying `buttons` -- and a button the browser thinks is
  // held down is the one explanation left standing. Keyboard events have no
  // such field, hence the guard.
  if (typeof event.buttons === "number") {
    state.lastWheelButtons = event.buttons;
    if (event.buttons !== 0) {
      state.wheelsWithButtonsHeld += 1;
    }
  }

  if (typeof event.clientX === "number") {
    state.lastPoint = { x: event.clientX, y: event.clientY };
    // A wheel event reports where the cursor is now, and it reaches us through
    // a different path than pointermove. If the cursor has moved since the last
    // pointer event, pointer events are being lost. If it is in the same place,
    // the operator is just scrolling with a parked mouse -- which is ordinary
    // behaviour and must never raise the banner.
    if (state.lastPointerPoint) {
      const movedX = Math.abs(event.clientX - state.lastPointerPoint.x);
      const movedY = Math.abs(event.clientY - state.lastPointerPoint.y);
      if (movedX > SILENT_MOVE_PX || movedY > SILENT_MOVE_PX) {
        state.pointerMovedSilently = true;
      }
    }

    // The flag above latches on a single wheel event and is only cleared by a
    // pointer event, so it proves the cursor moved silently at some earlier
    // instant -- not during the silence being reported. One fast move plus a
    // wheel in the same janked frame is enough to set it, after which a
    // genuinely parked mouse keeps it set for as long as the operator scrolls.
    // Comparing consecutive wheel events to each other measures movement
    // *inside* the silence, which is what the diagnosis actually needs. Both
    // are reported: this one grades the other, and a real freeze should carry
    // both. Detection still runs off the latch, so hardening the evidence
    // cannot cost us a report we would otherwise have had.
    // Reported alongside silentMoveMaxPx because a 0 means two different
    // things -- the cursor held still, or only one wheel ever landed inside the
    // window and there was nothing to compare it against.
    state.silentWheelSamples += 1;
    if (state.silentWindowPoint) {
      const windowX = Math.abs(event.clientX - state.silentWindowPoint.x);
      const windowY = Math.abs(event.clientY - state.silentWindowPoint.y);
      state.silentMoveMaxPx = Math.max(
        state.silentMoveMaxPx,
        Math.round(Math.max(windowX, windowY))
      );
      if (windowX > SILENT_MOVE_PX || windowY > SILENT_MOVE_PX) {
        state.pointerMovedSilentlyInWindow = true;
      }
    }
    state.silentWindowPoint = { x: event.clientX, y: event.clientY };
  }

  evaluate(state);
}

export function startInputWatchdog({
  onFreeze,
  onRecover,
  trackEvent,
  now = () => performance.now()
} = {}) {
  if (watchdog) {
    return watchdog;
  }

  const state = {
    now,
    startedAt: now(),
    counts: {},
    lastPointerEventAt: null,
    lastActivityAt: null,
    // Back-dated so the first evaluation is never throttled by a clock that
    // has barely started.
    lastEvaluatedAt: now() - EVALUATE_THROTTLE_MS,
    lastPointerId: null,
    lastPointerTarget: null,
    lastPoint: null,
    lastPointerPoint: null,
    pointerMovedSilently: false,
    pointerMovedSilentlyInWindow: false,
    silentMoveMaxPx: 0,
    silentWheelSamples: 0,
    silentWindowPoint: null,
    wheelsSinceLastPointer: 0,
    lastWheelButtons: null,
    wheelsWithButtonsHeld: 0,
    lastPointerButtons: null,
    pointerEventsSinceSample: 0,
    mismatches: 0,
    mismatchSince: null,
    activeSignature: null,
    reports: 0,
    reportsBySignature: {},
    lastReportAtBySignature: {},
    // Filled after boot, once, off an idle callback; see readHardware.
    hardware: null,
    hardwareRequested: false,
    hardwareIdleHandle: null,
    simulation: null,
    simulationDecoy: null,
    tickInterval: null,
    simulationTimeout: null,
    simulationEvaluateTimeout: null,
    simulationBlocker: null,
    simulationEscape: null,
    onFreeze,
    onRecover,
    trackEvent
  };

  const onPointer = event => recordPointer(state, event);
  const readHardwareOnce = () => {
    if (state.hardwareRequested) {
      return;
    }
    state.hardwareRequested = true;

    const read = () => {
      state.hardware = readHardware();
    };
    if (typeof window.requestIdleCallback === "function") {
      state.hardwareIdleHandle = window.requestIdleCallback(read, {
        timeout: 10000
      });
      return;
    }

    read();
  };
  const tick = () => {
    readHardwareOnce();
    evaluate(state);
  };
  const onActivity = event => recordActivity(state, event);
  const listenerOptions = { capture: true, passive: true };

  POINTER_TYPES.forEach(type =>
    window.addEventListener(type, onPointer, listenerOptions)
  );
  ACTIVITY_TYPES.forEach(type =>
    window.addEventListener(type, onActivity, listenerOptions)
  );
  state.tickInterval = setInterval(tick, TICK_MS);

  function clearSimulation() {
    if (!state.simulation) {
      return false;
    }

    if (state.simulationBlocker) {
      SIMULATED_BLOCK_TYPES.forEach(type =>
        document.removeEventListener(type, state.simulationBlocker, true)
      );
      state.simulationBlocker = null;
    }
    if (state.simulationEscape) {
      window.removeEventListener("keydown", state.simulationEscape, true);
      state.simulationEscape = null;
    }
    if (state.simulationTimeout) {
      clearTimeout(state.simulationTimeout);
      state.simulationTimeout = null;
    }
    if (state.simulationEvaluateTimeout) {
      clearTimeout(state.simulationEvaluateTimeout);
      state.simulationEvaluateTimeout = null;
    }
    state.simulationDecoy?.remove();
    state.simulationDecoy = null;
    state.simulation = null;
    state.activeSignature = null;
    state.mismatches = 0;
    state.mismatchSince = null;
    state.pointerMovedSilently = false;
    state.pointerMovedSilentlyInWindow = false;
    state.silentMoveMaxPx = 0;
    state.silentWheelSamples = 0;
    state.silentWindowPoint = null;
    state.wheelsSinceLastPointer = 0;
    state.pointerEventsSinceSample = 0;
    state.lastPointerEventAt = state.now();
    state.onRecover?.();
    return true;
  }

  watchdog = {
    // Reproduces the operator's experience on purpose: the page genuinely stops
    // responding to the mouse while scrolling and the keyboard keep working,
    // the real detector fires, and the real banner appears. Telemetry from a
    // simulation is flagged so it never pollutes the production signal.
    simulate(signature = SIGNATURE.inputLost) {
      if (!Object.values(SIGNATURE).includes(signature)) {
        throw new Error(
          `Unknown signature "${signature}". Use "${SIGNATURE.inputLost}" or "${SIGNATURE.pointerRetargeted}".`
        );
      }

      clearSimulation();
      state.simulation = signature;
      state.mismatches = 0;
      state.mismatchSince = null;
      state.activeSignature = null;
      state.reports = 0;
      state.lastActivityAt = state.now();

      if (signature === SIGNATURE.pointerRetargeted) {
        // Stands in for the element holding the capture. Zero-sized and inert,
        // so it changes nothing the operator can see or click.
        const decoy = document.createElement("div");
        decoy.setAttribute("data-input-watchdog-decoy", "");
        decoy.style.cssText = "position:fixed;width:0;height:0;pointer-events:none";
        document.body.appendChild(decoy);
        state.simulationDecoy = decoy;
      }

      state.simulationBlocker = event => {
        // Enter or Space on a focused control makes the browser dispatch a
        // click with no pointer behind it. Those must still get through: the
        // real failure loses mouse input only, and finishing the
        // identification from the keyboard is the whole point of the banner.
        if (isKeyboardActivated(event)) {
          return;
        }

        event.stopImmediatePropagation();
        if (event.cancelable) {
          event.preventDefault();
        }
      };
      // Registered on `document`, so the watchdog's own `window` capture
      // listeners still observe the events the app no longer receives.
      SIMULATED_BLOCK_TYPES.forEach(type =>
        document.addEventListener(type, state.simulationBlocker, true)
      );

      // The way out has to belong to the simulation itself. It lived in a UI
      // component once, and removing that component silently left the promise
      // in the message below with nothing listening to it. Not prevented or
      // stopped: Escape means things elsewhere in the app.
      state.simulationEscape = event => {
        if (event.key !== "Escape") {
          return;
        }

        clearSimulation();
      };
      window.addEventListener("keydown", state.simulationEscape, true);

      state.simulationTimeout = setTimeout(clearSimulation, SIMULATION_MAX_MS);

      // Pre-seeded to the threshold so the demo trips straight away, instead of
      // waiting for the operator to work a mouse that no longer responds.
      state.lastActivityAt = state.now();
      if (signature === SIGNATURE.inputLost) {
        state.lastPointerEventAt = state.now() - POINTER_SILENCE_MS - 1;
        state.pointerMovedSilently = true;
        // The demo has to satisfy the same evidence the real detector needs,
        // or it stops reproducing what production will actually report.
        state.pointerMovedSilentlyInWindow = true;
        state.silentWheelSamples = 2;
        state.silentMoveMaxPx = SILENT_MOVE_PX * 2;
      } else {
        state.lastPointerEventAt = state.now();
        state.lastPointerButtons = 0;
        state.mismatches = MISMATCH_LIMIT;
        state.mismatchSince = state.now() - MISMATCH_MS - 1;
      }

      state.simulationEvaluateTimeout = setTimeout(() => {
        state.lastActivityAt = state.now();
        state.lastEvaluatedAt = 0;
        evaluate(state);
      }, EVALUATE_THROTTLE_MS);

      return `Simulating "${signature}". The mouse is dead on purpose -- scroll and the keyboard still work. Press Esc to end it (it also ends by itself in ${SIMULATION_MAX_MS / 1000}s).`;
    },
    endSimulation: clearSimulation,
    isSimulating: () => !!state.simulation,
    snapshot: () => buildSnapshot(state, state.activeSignature),
    state: () => ({
      counts: { ...state.counts },
      mismatches: state.mismatches,
      mismatchSince: state.mismatchSince,
      activeSignature: state.activeSignature,
      simulation: state.simulation,
      reports: state.reports,
      reportsBySignature: { ...state.reportsBySignature },
      hardware: state.hardware && { ...state.hardware }
    }),
    stop() {
      clearSimulation();
      clearInterval(state.tickInterval);
      state.tickInterval = null;
      if (state.hardwareIdleHandle !== null) {
        window.cancelIdleCallback?.(state.hardwareIdleHandle);
        state.hardwareIdleHandle = null;
      }
      POINTER_TYPES.forEach(type =>
        window.removeEventListener(type, onPointer, true)
      );
      ACTIVITY_TYPES.forEach(type =>
        window.removeEventListener(type, onActivity, true)
      );
      if (window.arbitriumInputWatchdog === watchdog) {
        delete window.arbitriumInputWatchdog;
      }
      watchdog = null;
    }
  };

  // Exposed so the freeze can be demonstrated and inspected from the console on
  // any environment, including production, where this only ever reproduces.
  window.arbitriumInputWatchdog = watchdog;

  return watchdog;
}

export function getInputWatchdog() {
  return watchdog;
}

export {
  SIGNATURE,
  POINTER_SILENCE_MS,
  MISMATCH_LIMIT,
  MISMATCH_MS,
  SILENT_MOVE_PX,
  REPORT_COOLDOWN_MS,
  MAX_REPORTS,
  SIMULATION_MAX_MS
};
